<template>
  <div class="p-chat-container">
    <div class="p-chat-container__icon" @click="handleToggleChat">
      <p-icon icon="support" size="lg" color="fg-fix-white" />
    </div>

    <div
      v-if="chat.isPanelOpen"
      :class="[
        'p-chat-container__content-wrapper',
        { 'p-chat-container__content-wrapper--guest-info': chat.phase === 'guest-info' },
      ]"
    >
      <!-- ── Header ─────────────────────────────────────────────────── -->
      <header class="p-chat-container__header">
        <div class="p-chat-container__header-logomark" aria-hidden="true">
          <!-- Paribu "P" logomark: green circle matching Figma node 5192:30798 -->
          <span class="p-chat-container__header-logomark-letter f-utility-12-medium">P</span>
        </div>
        <span class="p-chat-container__header-title f-body-16-medium t-fg-primary">
          {{ $t('chat.assistant') }}
        </span>
        <button
          class="p-chat-container__header-close"
          type="button"
          :aria-label="$t('general.cancel')"
          @click="handleCloseClick"
        >
          <p-icon icon="x-close-delete" size="md" color="fg-primary" />
        </button>
      </header>

      <!-- ── Body ──────────────────────────────────────────────────── -->
      <div ref="chatContentRef" class="p-chat-container__content">
        <!-- Guest info form -->
        <template v-if="chat.phase === 'guest-info'">
          <p-guest-info-form
            :loading="chat.loading"
            :error="chat.error"
            @submit="(name, email, phone, tckn) => chat.connectAsGuest(name, email, phone, tckn)"
          />
        </template>

        <!-- Subject picker: new PChatSubjectPicker component (sub-group 2) -->
        <template v-else-if="chat.phase === 'subject-pick'">
          <p-chat-date-divider :label="$t('chat.date.today')" variant="today" />
          <p-chat-item :message-item="welcomeMessageItem" />
          <p-chat-subject-picker
            :subjects="chat.subjects"
            :loading="chat.loading"
            @start="(subjectId, firstMessage) => chat.startNewChat(subjectId, firstMessage)"
          />
        </template>

        <!-- Main chat view -->
        <template v-else>
          <template v-if="groupedMessages.length > 0">
            <template v-for="group in groupedMessages" :key="group.date">
              <!-- Date divider (PChatDateDivider from sub-group 1) -->
              <p-chat-date-divider :label="group.dateLabel" variant="today" />

              <template v-for="m in group.messages" :key="m.id">
                <!-- System messages (PChatSystemMessage from sub-group 1) -->
                <p-chat-system-message v-if="m.type === 'system'" :text="systemMessageText(m)" />
                <!-- Regular chat bubbles -->
                <p-chat-item v-else :message-item="toMessageItem(m)" />
              </template>
            </template>
          </template>
          <div v-else-if="!chat.loading" class="p-chat-container__empty">
            <p class="f-utility-14 t-fg-tertiary">{{ $t('chat.empty') }}</p>
          </div>

          <!-- Inline rating row: prompt + star picker -->
          <div v-if="chat.phase === 'rating'" class="p-chat-container__rating-row">
            <p
              class="p-chat-container__rating-prompt f-utility-14 t-fg-secondary text-center pb-8"
              v-text="$t('chat.feedback.prompt')"
            />
            <p-chat-star-rating :rating="chat.rating" @rate="(stars) => chat.submitRating(stars)" />
          </div>

          <!-- Resolved thanks message -->
          <p
            v-if="chat.phase === 'resolved'"
            class="p-chat-container__resolved-message f-utility-14 t-fg-primary text-center"
            v-text="$t('chat.feedback.thanksMessage')"
          />

          <!-- Status banner when chat is closed (PChatStatusBanner from sub-group 1) -->
          <p-chat-status-banner v-if="chat.phase === 'closed'" :text="$t('chat.ticketClosed')" />
        </template>

        <!-- Typing indicator: 3 animated dots in an inbound bubble -->
        <div v-if="chat.isTyping" class="p-chat-item p-chat-item--inbound">
          <div class="p-chat-item__bubble p-chat-item__bubble--inbound p-chat-item__bubble--typing">
            <span class="p-chat-item__dot" />
            <span class="p-chat-item__dot" />
            <span class="p-chat-item__dot" />
          </div>
        </div>
      </div>

      <!-- ── Input footer ───────────────────────────────────────────── -->
      <div
        v-if="chat.phase === 'chat' || chat.phase === 'subject-pick'"
        class="p-chat-container__bottom"
      >
        <p-file-text-input
          :attachment-disabled="chat.phase !== 'chat'"
          :loading="isSending"
          @send="handleInputSend"
          @typing="(t) => chat.sendTyping(t)"
          @attach="handleInputAttach"
        />
      </div>

      <!-- ── Feedback form bottom-sheet overlay ──────────────────────── -->
      <div
        v-if="chat.phase === 'rating' && chat.rating > 0"
        class="p-chat-container__feedback-overlay"
      >
        <div class="p-chat-container__feedback-overlay-sheet">
          <p-feedback-form
            :choices="chat.feedbackChoices"
            @submit="(choiceIds, comment) => chat.submitFeedback(choiceIds, comment)"
            @later="chat.dismissFeedbackForm()"
          />
        </div>
      </div>

      <!-- ── End-conversation modal ─────────────────────────────────── -->
      <div v-if="isShowCloseChatDialog" class="p-chat-container__close-chat-dialog">
        <div class="p-chat-container__close-chat-dialog-sheet">
          <p-icon
            icon="alert-error"
            size="xl"
            color="fg-danger"
            class="p-chat-container__close-chat-dialog-icon"
          />
          <div class="p-chat-container__close-chat-dialog-body">
            <h3 class="p-chat-container__close-chat-dialog-title f-title-40-bold t-fg-primary">
              {{ $t('chat.closeDialog.title') }}
            </h3>
            <p class="f-body-14 t-fg-primary">
              {{ $t('chat.closeDialog.description') }}
            </p>
          </div>
          <div class="p-chat-container__close-chat-dialog-actions">
            <p-button class="full-width" size="lg" @click="isShowCloseChatDialog = false">
              {{ $t('chat.closeDialog.continueButton') }}
            </p-button>
            <p-button
              class="full-width p-chat-container__close-chat-dialog-leave"
              size="lg"
              button-type="ghost"
              @click="handleLeaveConversation"
            >
              {{ $t('chat.closeDialog.leaveButton') }}
            </p-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useHelpdeskChatStore, watchHelpdeskLocale, type ViewMessage } from '@/store/helpdeskChat'

const chat = useHelpdeskChatStore()
const { t: $t } = useI18n()

const isShowCloseChatDialog = ref(false)
const chatContentRef = ref<HTMLElement | null>(null)

const handleToggleChat = () => {
  if (chat.isPanelOpen) {
    chat.closePanel()
    return
  }
  chat.openPanel()
  // Anonymous user: connect() landed on phase='guest-info' on mount.
  // Opening the panel just reveals the guest form. They submit it,
  // connectAsGuest() runs, then we fall into a fresh subject-pick.
  if (chat.phase === 'guest-info') return
  // Authenticated user: every FAB open starts a fresh subject-pick.
  // Existing tickets are reached from /ticket via loadChat.
  chat.startNewSubjectPick()
}

const handleLeaveConversation = () => {
  // Leaving the panel ≠ terminating the chat. The ticket stays open
  // server-side and is recoverable from /ticket. Samaritan parity:
  // EndConversationBS just calls router.dismiss(); endChat is not in
  // the close path.
  chat.closePanel()
  isShowCloseChatDialog.value = false
}

const handleCloseClick = () => {
  // Skip the confirmation modal only on guest-info (form not yet
  // submitted, no chat to lose). All other phases — including
  // subject-pick — still prompt before closing.
  if (chat.phase === 'guest-info') {
    chat.closePanel()
    return
  }
  isShowCloseChatDialog.value = true
}

const dayKey = (unixSeconds: number): string => {
  const d = new Date(unixSeconds * 1000)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

const formatDateLabel = (timeKey: string): string => {
  const [y, m, d] = timeKey.split('-').map(Number)
  const target = new Date(y, m, d)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  if (sameDay(target, today)) return $t('chat.date.today')
  if (sameDay(target, yesterday)) return $t('chat.date.yesterday')
  return `${String(d).padStart(2, '0')}.${String(m + 1).padStart(2, '0')}.${y}`
}

const groupedMessages = computed(() => {
  if (chat.messages.length === 0) return []
  const groups: Array<{ date: string; dateLabel: string; messages: ViewMessage[] }> = []
  let currentGroup: { date: string; dateLabel: string; messages: ViewMessage[] } | null = null

  for (const message of chat.messages) {
    const key = dayKey(message.timestamp)
    if (!currentGroup || currentGroup.date !== key) {
      currentGroup = { date: key, dateLabel: formatDateLabel(key), messages: [message] }
      groups.push(currentGroup)
    } else {
      currentGroup.messages.push(message)
    }
  }
  return groups
})

interface PChatMessageItem {
  type: 'sender' | 'receiver' | 'bot' | 'link' | 'file' | 'question' | 'open-form'
  message: string
  time?: string
  // True once an agent has edited the message — renders the "düzenlendi"
  // label next to the timestamp.
  edited?: boolean
  attachments?: Array<{
    id: string | number
    type: 'image' | 'file'
    uri?: string
    fileName: string
    mimeType: string
    sizeBytes?: number
  }>
}

const toMessageItem = (m: ViewMessage): PChatMessageItem => ({
  type: m.type === 'user' ? 'sender' : m.authorRole === 'bot' ? 'bot' : 'receiver',
  message: m.text,
  time: m.time,
  edited: m.editedAt != null,
  attachments: m.attachments,
})

const welcomeMessageItem = computed<PChatMessageItem>(() => ({
  type: 'receiver',
  message: `${$t('chat.welcome.title')}\n\n${$t('chat.welcome.description')}`,
  time: '',
}))

const isSending = ref(false)

async function withSending<T>(fn: () => Promise<T>): Promise<T | undefined> {
  if (isSending.value) return
  isSending.value = true
  try {
    return await fn()
  } finally {
    isSending.value = false
  }
}

const handleInputSend = (text: string) => {
  if (chat.phase === 'subject-pick') {
    // User typed straight into the chat bar without picking a category —
    // pass null so the store falls to the catchall subject (BE-marked
    // implicit fallback). Was previously falling to chat.subjects[0]
    // which silently sent the first non-catchall subject id (e.g. 1).
    withSending(() => chat.startNewChat(null, text))
    return
  }
  withSending(() => chat.sendMessage(text))
}

const handleInputAttach = (file: File) => {
  withSending(() => chat.sendAttachment(file))
}

const systemMessageText = (m: ViewMessage): string => {
  if (m.systemKind === 'participant_joined' && m.systemMeta?.name) {
    return $t('chat.system.participantJoined', { name: m.systemMeta.name })
  }
  return m.text
}

const scrollToBottom = () => {
  // Multi-pass instant scroll: catches initial render plus a late pass at
  // 120ms for fonts / late content settling.
  const pin = () => {
    const el = chatContentRef.value
    if (!el) return
    el.scrollTop = el.scrollHeight
  }
  nextTick(() => {
    pin()
    requestAnimationFrame(pin)
    setTimeout(pin, 120)
  })
}

// Yalnız liste BÜYÜYÜNCE. Ajan eski bir mesajı sildiğinde uzunluk azalıyor ve
// koşulsuz bir `scrollToBottom()` geçmişi okumak için yukarı kaydırmış
// kullanıcıyı en alta fırlatıyordu — içerik kaldırmanın yapması gereken şeyin
// tam tersi.
watch(
  () => chat.messages.length,
  (length, previousLength) => {
    if (length > (previousLength ?? 0)) scrollToBottom()
  },
)

watch(
  () => chat.isTyping,
  (typing) => {
    if (typing) scrollToBottom()
  },
)

watch(
  () => chat.isPanelOpen,
  (open) => {
    if (open) scrollToBottom()
  },
)

// Phase changes can introduce inline UI (stars, thanks bubble, feedback
// form) that grows the content beyond the messages array — scroll the
// panel to the bottom so the new bits are visible.
watch(
  () => chat.phase,
  () => scrollToBottom(),
)

// Rating transitions don't change phase (e.g. "Daha sonra" sets rating
// back to 0 within phase='rating'), but they do swap the rendered view
// between form takeover and inline stars + messages. Scroll to bottom
// so the user lands at the latest message after the swap.
watch(
  () => chat.rating,
  () => scrollToBottom(),
)

let stopLocaleWatcher: (() => void) | null = null

onMounted(() => {
  stopLocaleWatcher = watchHelpdeskLocale()
  // Match samaritan: one WS for the component lifetime. Open on mount,
  // close on unmount. Panel toggle is just UI visibility.
  // connect() decides based on the token: it lands on phase='guest-info'
  // when the user is not authenticated, otherwise it bootstraps the WS.
  chat.connect()
})

onUnmounted(() => {
  if (stopLocaleWatcher) stopLocaleWatcher()
  chat.disconnect()
})
</script>

<style lang="scss" scoped>
.p-chat-container {
  &__subject-chips {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
  }

  &__subject-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: transparent;
    border: 1px solid var(--bg-secondary, #e5e5e5);
    border-radius: 999px;
    cursor: pointer;
    text-align: left;
    max-width: 100%;

    &:hover:not(:disabled) {
      background: var(--bg-secondary-hover, #f5f5f5);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
}
</style>
